import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { deleteOrganization, GetOrganizations } from '../../../api/General/organization';
import { OrganizationView } from '../../../models/general';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell
} from '../../../components/ui/Table';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import AppIcon from '../../../components/AppIcon';
import { Spinner } from '../../../components/ui/Spinner';
import { PageLayout } from '../../../components/ui/PageLayout';
import { Network } from 'lucide-react';

export default function Organizations() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState<OrganizationView[]>([]);
  const [items, setItems] = useState<OrganizationView[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    GetOrganizations().then((res) => {
      setOrganizations(res.payload);
      setItems(res.payload);
      setLoading(false);
    });
  }, []);

  const filterData = (value: string) => {
    setItems(organizations.filter((u) => (u.name ?? '').toLowerCase().includes(value.toLowerCase())));
  };

  return (
    <PageLayout
      title={t('organizations:Organizations')}
      icon={Network}
      accentColor="slate"
      headerActions={(
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            iconName="Plus"
            onClick={() => navigate('/settings/organizations/add')}
          >
            {t('organizations:Add Organization')}
          </Button>
          <Button
            variant="outline"
            iconName="Send"
            onClick={() => {
              // Implement SendQueuedEmails function
            }}
          >
            {t('common:Send Queued Emails')}
          </Button>
        </div>
      )}
    >
      <div className="flex flex-col">
      <div className="flex items-center justify-between gap-4 border-b border-border px-4 py-3">
        <div className="flex items-center gap-2">
          <div className="relative">
            <AppIcon name="Search" size={16} className="absolute start-2.5 top-2.5 text-muted-foreground" />
            <Input
              placeholder={t('common:Search')}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => filterData(e.target.value ?? '')}
              className="ps-8"
            />
          </div>
        </div>
      </div>

      <div className="flex-1">
        <div className="rounded-lg border border-border bg-card">
          {loading ? (
            <div className="flex justify-center p-6">
              <Spinner size="lg" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('common:Name')}</TableHead>
                  <TableHead>{t('organizations:Number of companies')}</TableHead>
                  <TableHead>{t('organizations:Options')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.name}</TableCell>
                    <TableCell>{item.numberofUsers}</TableCell>
                    <TableCell>
                      <div className="flex gap-2.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          title={t('organizations:Companies')}
                          asChild
                        >
                          <Link to={`/organizations/${item.id}/companies`}>
                            <AppIcon name="Table2" size={16} />
                          </Link>
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          title={t('organizations:Archive')}
                          onClick={async () => {
                            if (window.confirm(t('organizations:Are you sure you want to delete this company?'))) {
                              await deleteOrganization(item.id);
                            }
                          }}
                        >
                          <AppIcon name="Trash2" size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center">
                      {t('organizations:No organizations found')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
      </div>
    </PageLayout>
  );
}
